import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeCheck, Heart, Home, LogOut, MapPin, ShieldCheck, Users, Loader2, Sparkles } from 'lucide-react';
import { deleteUser } from 'firebase/auth';
import { deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '@context/AuthContext';
import { useUser } from '@hooks/useUsers';
import { firestore } from '@config/firebase';
import BottomNav from '@components/BottomNav';
import TenantNav from '../../components/TenantNav';
import { PetsPreference, SmokingPreference } from '../../types/user.types';
import { getProvinces, getMetrosForProvince, getNeighborhoodsForMetro, type Province } from '@utils/locations';

type ProfileForm = {
  name: string;
  city: string; // Legacy
  province: string;
  provinceCode: string;
  metro: string;
  occupation: string;
  budgetMin: number;
  budgetMax: number;
  bio: string;
  vibe: string;
  dailyRhythms: string[];
  sleepSchedule: 'early' | 'flexible' | 'night' | null;
  cleanliness: 'tidy' | 'balanced' | 'relaxed' | null;
  lifestyle: string[];
  interests: string[];
  petsPreference: PetsPreference;
  smokingPreference: SmokingPreference;
};

const vibeOptions = [
  'Calm & considerate',
  'Social but chill',
  'Neat and balanced',
  'Up for shared dinners',
  'Respectful of space',
];

const sleepOptions: { key: 'early' | 'flexible' | 'night'; label: string }[] = [
  { key: 'early', label: 'Early riser' },
  { key: 'flexible', label: 'Flexible' },
  { key: 'night', label: 'Night owl' },
];

const cleanlinessOptions: { key: 'tidy' | 'balanced' | 'relaxed'; label: string }[] = [
  { key: 'tidy', label: 'Tidy & organized' },
  { key: 'balanced', label: 'Balanced' },
  { key: 'relaxed', label: 'More relaxed' },
];

const lifestyleOptions = [
  'Remote-friendly',
  'Quiet weeknights',
  'Shared dinners weekly',
  'Organized kitchen',
  'No indoor smoking',
  'Early mornings',
  'Low-noise after 10pm',
];

const interestOptions = [
  'Pilates',
  'Photography',
  'Sunday markets',
  'Live music',
  'Trail runs',
  'Board games',
  'Cooking',
  'Ocean walks',
];

const Profile: React.FC = () => {
  const { currentUser, logout, resendVerification } = useAuth();
  const navigate = useNavigate();
  const { user, loading } = useUser(currentUser?.uid);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifySending, setVerifySending] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState<string | null>(null);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [availableMetros, setAvailableMetros] = useState<string[]>([]);
  const [availableNeighborhoods, setAvailableNeighborhoods] = useState<string[]>([]);
  const [form, setForm] = useState<ProfileForm>({
    name: '',
    city: '',
    province: '',
    provinceCode: '',
    metro: '',
    occupation: '',
    budgetMin: 0,
    budgetMax: 0,
    bio: '',
    vibe: '',
    dailyRhythms: [],
    sleepSchedule: null,
    cleanliness: null,
    lifestyle: [],
    interests: [],
    petsPreference: 'negotiable',
    smokingPreference: 'outside-only',
  });

  useEffect(() => {
    async function loadProvinces() {
      const data = await getProvinces();
      setProvinces(data);
    }
    loadProvinces();
  }, []);

  useEffect(() => {
    if (user) {
      // Backward compat: older profiles stored a single rhythm in `vibe`.
      let dailyRhythms: string[] = [];
      if (user.dailyRhythms && user.dailyRhythms.length > 0) {
        dailyRhythms = user.dailyRhythms;
      } else if (user.vibe && vibeOptions.includes(user.vibe)) {
        dailyRhythms = [user.vibe];
      }

      setForm({
        name: user.name || '',
        city: user.city || '',
        province: user.province || '',
        provinceCode: user.provinceCode || '',
        metro: user.metro || '',
        occupation: user.occupation || '',
        budgetMin: user.budgetMin || 0,
        budgetMax: user.budgetMax || 0,
        bio: user.bio || '',
        vibe: user.vibe || '',
        dailyRhythms,
        sleepSchedule: user.sleepSchedule || null,
        cleanliness: user.cleanliness || null,
        lifestyle: user.lifestyle || [],
        interests: user.interests || [],
        petsPreference: user.petsPreference || 'negotiable',
        smokingPreference: user.smokingPreference || 'outside-only',
      });

      // Load metros and neighborhoods if province/metro are set
      if (user.provinceCode) {
        getMetrosForProvince(user.provinceCode).then(setAvailableMetros);
      }
      if (user.metro) {
        setAvailableNeighborhoods(getNeighborhoodsForMetro(user.metro));
      }
    }
  }, [user]);

  const toggleChip = (key: 'lifestyle' | 'interests' | 'dailyRhythms', value: string) => {
    setForm((prev) => {
      const exists = prev[key].includes(value);
      const next = exists ? prev[key].filter((item) => item !== value) : [...prev[key], value];
      return { ...prev, [key]: next };
    });
  };

  const handleProvinceChange = async (provinceCode: string) => {
    const selectedProvince = provinces.find((p) => p.code === provinceCode);
    if (selectedProvince) {
      setForm((prev) => ({
        ...prev,
        provinceCode,
        province: selectedProvince.name,
        metro: '',
      }));

      const metros = await getMetrosForProvince(provinceCode);
      setAvailableMetros(metros);
      setAvailableNeighborhoods([]);
    }
  };

  const handleMetroChange = (metro: string) => {
    setForm((prev) => ({
      ...prev,
      metro,
      city: metro, // Update legacy field
    }));

    const neighborhoods = getNeighborhoodsForMetro(metro);
    setAvailableNeighborhoods(neighborhoods);
  };

  const readiness = useMemo(() => {
    const fields = [form.name, form.province || form.city, form.occupation, form.bio];
    const basicsFilled = fields.every((field) => field.trim().length > 1);
    const budgetSet = form.budgetMin > 0 && form.budgetMax > form.budgetMin;
    const addedSignals = form.lifestyle.length >= 2 && form.interests.length >= 2;
    return Math.min(100, [basicsFilled, budgetSet, addedSignals].filter(Boolean).length * 33 + 1);
  }, [form]);

  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteAccount = async () => {
    if (!currentUser) return;
    const confirmed = window.confirm(
      'Delete your RoomZa account? This removes your profile and cannot be undone. ' +
        'Messages other people already received may be retained as described in the Privacy Policy.'
    );
    if (!confirmed) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDoc(doc(firestore, 'users', currentUser.uid));
      await deleteUser(currentUser);
      // Auth state change will redirect via the route guards.
      navigate('/');
    } catch (err) {
      console.error('Error deleting account', err);
      const code = (err as { code?: string })?.code;
      if (code === 'auth/requires-recent-login') {
        setDeleteError('For your security, please log out and log back in, then try deleting again.');
      } else {
        setDeleteError('Could not delete your account right now. Please try again or contact support.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleResendVerification = async () => {
    setVerifySending(true);
    setVerifyStatus(null);
    try {
      await resendVerification();
      setVerifyStatus('Verification email sent. Check your inbox (and spam).');
    } catch (err) {
      console.error('Error sending verification email', err);
      setVerifyStatus('Could not send the email right now. Please try again shortly.');
    } finally {
      setVerifySending(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) {
      setError('Please sign in to update your profile.');
      return;
    }
    setSaving(true);
    setStatus(null);
    setError(null);

    try {
      await setDoc(doc(firestore, 'users', currentUser.uid), {
        role: 'roommate_seeker',
        name: form.name.trim(),
        city: form.metro || form.city.trim(),
        province: form.province,
        provinceCode: form.provinceCode,
        metro: form.metro,
        occupation: form.occupation.trim(),
        budgetMin: Number(form.budgetMin) || 0,
        budgetMax: Number(form.budgetMax) || 0,
        bio: form.bio.trim(),
        vibe: form.vibe.trim(),
        dailyRhythms: form.dailyRhythms,
        sleepSchedule: form.sleepSchedule,
        cleanliness: form.cleanliness,
        lifestyle: form.lifestyle,
        interests: form.interests,
        petsPreference: form.petsPreference,
        smokingPreference: form.smokingPreference,
        profileComplete: true,
        isActive: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setStatus('Saved. Your profile is live for matching.');
    } catch (err) {
      console.error('Error updating profile', err);
      setError('Could not save your profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-primary px-4 pb-24">
        <div className="text-center space-y-2">
          <p className="text-lg font-semibold text-text-primary">Sign in to view your profile.</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 py-10 pb-24">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -right-20 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-10 left-8 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto space-y-6">
        <TenantNav />
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3 md:gap-4">
          <div>
            <p className="text-xs sm:text-sm uppercase tracking-wide text-text-muted font-semibold">Profile</p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text-primary">Your roommate-ready profile</h1>
            <p className="text-sm md:text-base text-text-secondary mt-1 md:mt-2">Update details so matches see the real you.</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button className="btn-outline flex-1 sm:flex-none text-sm py-2 px-3" onClick={() => navigate('/tenant/onboarding')}>
              Edit via wizard
            </button>
            <button className="btn-primary flex-1 sm:flex-none text-sm py-2 px-3" onClick={() => navigate('/tenant/swipe')}>
              View swipe deck
            </button>
          </div>
        </div>

        {loading ? (
          <div className="card p-6 text-text-secondary flex items-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" /> Fetching your profile...
          </div>
        ) : (
          <>
            <div className="card p-6 flex flex-wrap items-start gap-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary-200 to-accent-purple text-white flex items-center justify-center text-3xl font-extrabold">
                {form.name ? form.name.charAt(0) : 'U'}
              </div>
              <div className="flex-1 min-w-[240px] space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="text-3xl font-extrabold text-text-primary">{form.name || 'Add your name'}</h2>
                  <span className="px-3 py-1 rounded-full bg-primary-50 text-primary-300 text-xs font-semibold flex items-center gap-1">
                    <BadgeCheck className="w-4 h-4" /> Ready for matches
                  </span>
                </div>
                <p className="text-text-secondary flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {form.city || 'Add city'} · Budget R{form.budgetMin || 0} - R{form.budgetMax || 0}
                </p>
                <p className="text-text-secondary">{form.occupation || 'Add what you do'}.</p>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-2 rounded-full text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30">
                    R{form.budgetMin || 0} - R{form.budgetMax || 0} budget
                  </span>
                  <span className="px-3 py-2 rounded-full text-xs font-semibold bg-accent-blue/10 text-accent-blue border border-accent-blue/30">
                    {form.city || 'Where are you looking?'}
                  </span>
                  <span className="px-3 py-2 rounded-full text-xs font-semibold bg-primary-50 text-primary-300 border border-primary-100">
                    {form.lifestyle[0] || 'Add a lifestyle signal'}
                  </span>
                </div>
              </div>
              <div className="px-5 py-4 rounded-2xl bg-gradient-to-br from-primary-200 to-accent-purple text-white shadow-lg">
                <p className="text-sm text-white/80">Match readiness</p>
                <p className="text-3xl font-extrabold">{readiness}%</p>
                <p className="text-sm text-white/80">Complete details and keep signals fresh.</p>
              </div>
            </div>

            {(error || status) && (
              <div
                className={`p-4 rounded-xl border-2 ${
                  error ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-semantic-success text-semantic-success'
                }`}
              >
                {error || status}
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-white border-2 border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-text-primary font-semibold mb-2">
                  <ShieldCheck className="w-4 h-4 text-primary-300" /> Identity & trust
                </div>
                <p className="text-text-secondary text-sm">Profile details synced with your account.</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {currentUser.emailVerified ? (
                    <span className="px-3 py-2 rounded-full text-xs font-semibold bg-green-50 text-semantic-success border border-semantic-success">
                      Email verified
                    </span>
                  ) : (
                    <span className="px-3 py-2 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                      Email not verified
                    </span>
                  )}
                  {user?.isActive ? (
                    <span className="px-3 py-2 rounded-full text-xs font-semibold bg-primary-50 text-primary-300 border border-primary-100">
                      Active profile
                    </span>
                  ) : (
                    <span className="px-3 py-2 rounded-full text-xs font-semibold bg-gray-50 text-text-muted border border-gray-200">
                      Profile inactive
                    </span>
                  )}
                </div>
                {!currentUser.emailVerified && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={verifySending}
                      className="text-xs font-semibold text-primary-300 underline underline-offset-2 hover:text-primary-400 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {verifySending ? 'Sending…' : 'Resend verification email'}
                    </button>
                    {verifyStatus && (
                      <p className="text-xs text-text-secondary mt-1">{verifyStatus}</p>
                    )}
                  </div>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white border-2 border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-text-primary font-semibold mb-2">
                  <Heart className="w-4 h-4 text-primary-300" /> Lifestyle
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.lifestyle.length ? (
                    form.lifestyle.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-2 rounded-full text-xs font-semibold bg-white border-2 border-primary-100 text-text-primary"
                      >
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-text-muted">Add a couple of signals below.</span>
                  )}
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white border-2 border-gray-100 shadow-sm">
                <div className="flex items-center gap-2 text-text-primary font-semibold mb-2">
                  <Users className="w-4 h-4 text-primary-300" /> Interests
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.interests.length ? (
                    form.interests.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-2 rounded-full text-xs font-semibold bg-accent-purple/10 text-accent-purple border border-accent-purple/30"
                      >
                        {item}
                      </span>
                    ))
                  ) : (
                    <span className="text-sm text-text-muted">Add a few so people can connect.</span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="card p-5 space-y-4">
                <div className="flex items-center gap-2 text-text-primary font-semibold">
                  <Home className="w-4 h-4 text-primary-300" /> Basics
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="profile-name" className="block text-sm font-semibold text-text-primary mb-2">Full name</label>
                    <input
                      id="profile-name"
                      className="input"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <span className="block text-sm font-semibold text-text-primary mb-2">Location</span>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <select
                          aria-label="Province"
                          className="input"
                          value={form.provinceCode}
                          onChange={(e) => handleProvinceChange(e.target.value)}
                        >
                          <option value="">Select province</option>
                          {provinces.map((province) => (
                            <option key={province.code} value={province.code}>
                              {province.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <select
                          aria-label="Metro or city"
                          className="input"
                          value={form.metro}
                          onChange={(e) => handleMetroChange(e.target.value)}
                          disabled={!form.provinceCode}
                        >
                          <option value="">Select metro/city</option>
                          {availableMetros.map((metro) => (
                            <option key={metro} value={metro}>
                              {metro}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {!form.provinceCode && form.city && (
                      <p className="text-xs text-text-muted mt-2">
                        Legacy location: {form.city}. Please select province and metro above.
                      </p>
                    )}
                    {availableNeighborhoods.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-text-muted mb-2">
                          Popular neighborhoods in {form.metro}
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          {availableNeighborhoods.slice(0, 6).map((neighborhood) => (
                            <span
                              key={neighborhood}
                              className="px-2 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-300 border border-primary-100"
                            >
                              {neighborhood}
                            </span>
                          ))}
                          {availableNeighborhoods.length > 6 && (
                            <span className="px-2 py-1 text-xs text-text-muted">
                              +{availableNeighborhoods.length - 6} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label htmlFor="profile-occupation" className="block text-sm font-semibold text-text-primary mb-2">Occupation</label>
                    <input
                      id="profile-occupation"
                      className="input"
                      value={form.occupation}
                      onChange={(e) => setForm((prev) => ({ ...prev, occupation: e.target.value }))}
                      placeholder="e.g. Product designer (remote)"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label htmlFor="profile-budget-min" className="block text-sm font-semibold text-text-primary mb-2">Budget min (R)</label>
                      <input
                        id="profile-budget-min"
                        type="number"
                        className="input"
                        value={form.budgetMin}
                        onChange={(e) => setForm((prev) => ({ ...prev, budgetMin: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label htmlFor="profile-budget-max" className="block text-sm font-semibold text-text-primary mb-2">Budget max (R)</label>
                      <input
                        id="profile-budget-max"
                        type="number"
                        className="input"
                        value={form.budgetMax}
                        onChange={(e) => setForm((prev) => ({ ...prev, budgetMax: Number(e.target.value) }))}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="profile-bio" className="block text-sm font-semibold text-text-primary mb-2">Bio</label>
                  <textarea
                    id="profile-bio"
                    className="input min-h-[120px]"
                    value={form.bio}
                    onChange={(e) => setForm((prev) => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell roommates how you live, work, and recharge"
                  />
                </div>
              </div>

              <div className="card p-5 space-y-4">
                <div>
                  <label htmlFor="profile-vibe" className="block text-sm font-semibold text-text-primary mb-2">One-line vibe</label>
                  <input
                    id="profile-vibe"
                    className="input"
                    value={form.vibe}
                    onChange={(e) => setForm((prev) => ({ ...prev, vibe: e.target.value }))}
                    placeholder="Considerate, balanced, easy-going"
                  />
                </div>

                <div>
                  <span className="block text-sm font-semibold text-text-primary mb-1">Daily rhythm</span>
                  <p className="text-xs text-text-secondary mb-2">Pick all that fit</p>
                  <div className="flex flex-wrap gap-2">
                    {vibeOptions.map((vibe) => (
                      <button
                        key={vibe}
                        type="button"
                        onClick={() => toggleChip('dailyRhythms', vibe)}
                        aria-pressed={form.dailyRhythms.includes(vibe)}
                        className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                          form.dailyRhythms.includes(vibe)
                            ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                            : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                        }`}
                      >
                        {vibe}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <span className="block text-sm font-semibold text-text-primary mb-2">Sleep schedule</span>
                    <div className="flex flex-wrap gap-2">
                      {sleepOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, sleepSchedule: option.key }))}
                          aria-pressed={form.sleepSchedule === option.key}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border-2 transition-all duration-200 ${
                            form.sleepSchedule === option.key
                              ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                              : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-text-primary mb-2">Cleanliness</span>
                    <div className="flex flex-wrap gap-2">
                      {cleanlinessOptions.map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, cleanliness: option.key }))}
                          aria-pressed={form.cleanliness === option.key}
                          className={`px-2.5 py-1.5 rounded-full text-xs font-semibold border-2 transition-all duration-200 ${
                            form.cleanliness === option.key
                              ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                              : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-text-primary font-semibold">
                  <Sparkles className="w-4 h-4 text-primary-300" /> Lifestyle signals
                </div>
                <div className="flex flex-wrap gap-2">
                  {lifestyleOptions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleChip('lifestyle', item)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        form.lifestyle.includes(item)
                          ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                          : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-text-primary font-semibold">
                  <Users className="w-4 h-4 text-primary-300" /> Interests
                </div>
                <div className="flex flex-wrap gap-2">
                  {interestOptions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleChip('interests', item)}
                      className={`px-2.5 py-1.5 md:px-3 md:py-2 rounded-full text-xs md:text-sm font-semibold border-2 transition-all duration-200 ${
                        form.interests.includes(item)
                          ? 'bg-accent-purple/90 text-white border-accent-purple'
                          : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl bg-white border-2 border-gray-200">
                    <p className="text-sm font-semibold text-text-primary">Pets</p>
                    <div className="flex gap-1.5 md:gap-2 mt-2">
                      {(
                        [
                          { label: 'Yes', value: 'yes' },
                          { label: 'No', value: 'no' },
                          { label: 'Negotiable', value: 'negotiable' },
                        ] as { label: string; value: PetsPreference }[]
                      ).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, petsPreference: option.value }))}
                          className={`px-2 py-1.5 md:px-3 md:py-2 rounded-full text-xs font-semibold border-2 transition-all duration-200 flex-1 ${
                            form.petsPreference === option.value
                              ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                              : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-white border-2 border-gray-200">
                    <p className="text-sm font-semibold text-text-primary">Smoking</p>
                    <div className="flex gap-1.5 md:gap-2 mt-2">
                      {(
                        [
                          { label: 'No', value: 'no' },
                          { label: 'Outside only', value: 'outside-only' },
                          { label: 'Negotiable', value: 'negotiable' },
                        ] as { label: string; value: SmokingPreference }[]
                      ).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, smokingPreference: option.value }))}
                          className={`px-2 py-1.5 md:px-3 md:py-2 rounded-full text-xs font-semibold border-2 transition-all duration-200 flex-1 ${
                            form.smokingPreference === option.value
                              ? 'bg-primary-200 text-white border-primary-300 shadow-md'
                              : 'bg-white border-gray-200 text-text-secondary hover:border-primary-200'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <BadgeCheck className="w-5 h-5 text-primary-300 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-text-primary font-semibold text-sm md:text-base">Ready to match</p>
                  <p className="text-xs md:text-sm text-text-secondary">Save updates to sync with your swipe deck and matches.</p>
                </div>
              </div>
              <div className="flex gap-2 md:gap-3 w-full sm:w-auto">
                <button className="btn-outline flex-1 sm:flex-none text-sm md:text-base py-2 md:py-2.5" onClick={() => navigate('/tenant/matches')}>
                  See matches
                </button>
                <button className="btn-primary flex-1 sm:flex-none text-sm md:text-base py-2 md:py-2.5" onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save profile'}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Account actions */}
        <div className="mt-8 pb-6 space-y-3">
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-md border border-gray-200 rounded-2xl text-gray-700 font-semibold hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>

          <div className="rounded-2xl border-2 border-red-100 bg-red-50/60 p-4">
            <p className="text-sm font-semibold text-red-700">Delete my account</p>
            <p className="text-xs text-text-secondary mt-1">
              Permanently removes your profile. This cannot be undone.
            </p>
            {deleteError && (
              <p role="alert" className="text-xs text-red-600 mt-2">{deleteError}</p>
            )}
            <button
              onClick={handleDeleteAccount}
              disabled={deleting}
              className="mt-3 px-4 py-2 rounded-xl border-2 border-red-300 text-red-600 text-sm font-semibold hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {deleting ? 'Deleting…' : 'Delete my account'}
            </button>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Profile;
